import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, Trash2, ShoppingBag, Banknote, CheckCircle2, Clock, X, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useSettings } from "@/contexts/SettingsContext";
import { toast } from "sonner";

const EDIT_WINDOW = 300;
const STORE_KEY = "tulsi_active_order";

export default function OrderPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { items, updateQty, removeItem, clear, subtotal, tableNumber, setTableNumber } = useCart();
  const { settings } = useSettings();
  const { t } = useTranslation();

  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // QR deep-link: ?table=N → save table number, send user to /menu to browse
  useEffect(() => {
    const tbl = params.get("table");
    if (tbl) {
      setTableNumber(tbl);
      navigate("/menu", { replace: true });
      return;
    }
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (raw) {
        const stored = JSON.parse(raw);
        const createdAt = new Date(stored.created_at).getTime();
        const elapsed = (Date.now() - createdAt) / 1000;
        if (elapsed < EDIT_WINDOW && stored.status === "new") setPlacedOrder(stored);
        else localStorage.removeItem(STORE_KEY);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!placedOrder) return;
    const createdAt = new Date(placedOrder.created_at).getTime();
    const tick = () => {
      const elapsed = (Date.now() - createdAt) / 1000;
      setSecondsLeft(Math.max(0, Math.floor(EDIT_WINDOW - elapsed)));
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [placedOrder]);

  const editingOrderId = localStorage.getItem("tulsi_editing_order");

  const submitOrder = async () => {
    if (!tableNumber) return toast.error("Please enter your table number");
    if (items.length === 0) return toast.error("Your cart is empty — go add some dishes from the menu");
    setSubmitting(true);
    try {
      const payload = {
        table_number: parseInt(tableNumber, 10),
        customer_name: customerName || "Guest",
        customer_email: customerEmail,
        items: items.map((i) => ({ menu_item_id: i.menu_item_id, name: i.name, price: i.price, quantity: i.quantity })),
        notes,
      };
      let res;
      if (editingOrderId) {
        res = await api.put(`/orders/${editingOrderId}/customer-update`, payload);
        toast.success("Order updated!");
      } else {
        res = await api.post("/orders", payload);
        toast.success(t("order.orderPlaced"));
      }
      localStorage.removeItem("tulsi_editing_order");
      sessionStorage.removeItem("tulsi_edit_items");
      sessionStorage.removeItem("tulsi_edit_name");
      sessionStorage.removeItem("tulsi_edit_notes");
      setPlacedOrder(res.data);
      localStorage.setItem(STORE_KEY, JSON.stringify(res.data));
      clear();
      setNotes("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save order");
    } finally {
      setSubmitting(false);
    }
  };

  const editOrder = () => {
    // Put items back into cart so user can edit
    placedOrder.items.forEach((i) => {
      for (let k = 0; k < i.quantity; k++) {
        // re-add via cart context method indirectly — use addItem from useCart if available
      }
    });
    // Simpler: hand off to /menu with a flag, customer adds new items, saves via customer-update endpoint
    localStorage.setItem("tulsi_editing_order", placedOrder.id);
    sessionStorage.setItem("tulsi_edit_items", JSON.stringify(placedOrder.items));
    sessionStorage.setItem("tulsi_edit_name", placedOrder.customer_name || "");
    sessionStorage.setItem("tulsi_edit_notes", placedOrder.notes || "");
    navigate("/menu");
  };

  const cancelOrder = async () => {
    if (!window.confirm("Cancel this order?")) return;
    try {
      await api.post(`/orders/${placedOrder.id}/customer-cancel`);
      localStorage.removeItem(STORE_KEY);
      setPlacedOrder(null);
      toast.success("Order cancelled");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };

  const printReceipt = () => {
    const win = window.open("", "_blank", "width=400,height=600");
    if (!win) return toast.error("Pop-up blocked");
    const s = settings || {};
    const html = `<html><head><title>TULSI Receipt — Table ${placedOrder.table_number}</title>
      <style>@media print { @page { size: 80mm auto; margin: 4mm; } }
      body { font-family: 'Courier New', monospace; padding: 12px; max-width: 320px; margin: 0 auto; color: #000; background: #fff; }
      h1 { text-align: center; margin: 0; font-size: 22px; letter-spacing: 2px; }
      .sub { text-align: center; font-size: 11px; margin: 0 0 8px; letter-spacing: 2px; }
      .table { text-align: center; font-size: 36px; font-weight: bold; padding: 8px; border: 2px solid #000; margin: 12px 0; }
      .row { display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px dashed #999; font-size: 13px; }
      .total { font-size: 18px; font-weight: bold; padding: 8px 0; border-top: 2px solid #000; border-bottom: 2px solid #000; margin-top: 12px; }
      .footer { text-align: center; font-size: 10px; margin-top: 12px; }
      .note { font-style: italic; font-size: 12px; padding: 8px; border: 1px dashed #999; margin-top: 8px; }</style></head><body>
      <h1>TULSI 杜爾希</h1><p class="sub">KITCHEN RECEIPT</p>
      <div class="table">Table #${placedOrder.table_number}</div>
      <p style="font-size:12px;">Customer: <strong>${placedOrder.customer_name}</strong></p>
      <p style="font-size:11px;">Time: ${new Date(placedOrder.created_at).toLocaleString()}</p>
      <div style="margin-top:10px;">${placedOrder.items.map(i => `<div class="row"><span><strong>${i.quantity}×</strong> ${i.name}</span><span>NT$ ${(i.price*i.quantity).toFixed(0)}</span></div>`).join("")}</div>
      <div class="row total"><span>TOTAL</span><span>NT$ ${placedOrder.total.toFixed(0)}</span></div>
      <p class="sub" style="margin-top:8px;">— CASH ON PICKUP —</p>
      ${placedOrder.notes ? `<div class="note"><strong>NOTE:</strong> ${placedOrder.notes}</div>` : ""}
      <div class="footer">${s.phone || ""} · ${s.address || ""}<br>Order #${placedOrder.id.slice(0,8).toUpperCase()}</div>
      <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),500);}</script></body></html>`;
    win.document.write(html); win.document.close();
  };

  const fmtMMSS = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  // ============ CONFIRMATION ============
  if (placedOrder) {
    const canEdit = secondsLeft > 0 && placedOrder.status === "new";
    return (
      <div className="pt-32 pb-12 min-h-screen container-tulsi" data-testid="order-done-page">
        <div className="max-w-xl mx-auto glass-card p-8 text-center">
          <CheckCircle2 size={56} className="text-[#A90011] mx-auto mb-4" />
          <h1 className="font-serif-display text-4xl text-gold-gradient mb-2">{t("order.orderPlaced")}</h1>
          <p className="text-[#6B4A4E] mb-6">{t("order.onItsWay")}</p>
          <div className="p-5 rounded-lg bg-[#FBEBD8] border-2 border-[#C9A227]/40 mb-6">
            <p className="text-xs tracking-[0.3em] uppercase text-[#8B6F1A] mb-2">{t("order.table")}</p>
            <p className="font-serif-display text-7xl text-[#A90011] leading-none" data-testid="confirm-table-number">#{placedOrder.table_number}</p>
          </div>
          <div className="text-left bg-white border border-[#E8D9BE] rounded-md p-4 mb-4 text-sm">
            {placedOrder.items.map((i, idx) => (
              <div key={idx} className="flex justify-between text-[#6B4A4E] py-1">
                <span>{i.quantity} × {i.name}</span>
                <span className="text-[#2A0708]">NT$ {(i.price * i.quantity).toFixed(0)}</span>
              </div>
            ))}
            <div className="flex justify-between border-t border-[#E8D9BE] mt-2 pt-2 text-lg">
              <span className="font-serif-display text-[#2A0708]">{t("order.totalCash")}</span>
              <span className="font-serif-display text-[#A90011]">NT$ {placedOrder.total}</span>
            </div>
          </div>
          <p className="text-xs uppercase tracking-widest text-[#A90011] mb-6 flex items-center gap-2 justify-center"><Banknote size={14} /> {t("order.cashCounter")}</p>
          {canEdit && (
            <div className="border border-[#C9A227]/30 rounded-md p-3 mb-4 bg-[#FBEBD8]/40" data-testid="edit-window">
              <p className="text-xs tracking-widest uppercase text-[#8B6F1A] flex items-center justify-center gap-2">
                <Clock size={12} /> {t("order.editWindow", { seconds: fmtMMSS(secondsLeft) })}
              </p>
              <div className="flex gap-2 mt-3">
                <button onClick={editOrder} className="flex-1 text-xs tracking-widest uppercase border border-[#C9A227] text-[#8B6F1A] hover:bg-[#C9A227]/10 rounded px-3 py-2" data-testid="edit-meal-btn">
                  ✎ Edit Meal
                </button>
                <button onClick={cancelOrder} className="flex-1 text-xs tracking-widest uppercase border border-[#A90011]/40 text-[#A90011] hover:bg-[#A90011]/10 rounded px-3 py-2" data-testid="cancel-order-btn">
                  <X size={12} className="inline" /> {t("order.cancelOrder")}
                </button>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Link to="/" className="flex-1"><button className="btn-outline-gold w-full justify-center" data-testid="back-home-btn">{t("order.backHome")}</button></Link>
            <button onClick={() => { localStorage.removeItem(STORE_KEY); setPlacedOrder(null); navigate("/menu"); }} className="btn-saffron flex-1 justify-center" data-testid="new-order-btn">
              {t("order.placeAnother")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============ CHECKOUT ============
  return (
    <div className="pt-32 pb-12 min-h-screen" data-testid="order-page">
      <div className="container-tulsi max-w-2xl">
        <div className="text-center mb-10">
          <p className="text-[10px] tracking-[0.5em] uppercase text-[#A90011] mb-3 font-medium">— {t("order.tableService")} —</p>
          <h1 className="font-serif-display text-5xl text-gold-gradient leading-[1.05]">{t("order.title")}</h1>
          <div className="gold-separator" />
        </div>

        <div className="glass-card-strong rounded-2xl p-6">
          <h3 className="font-serif-display text-2xl text-[#2A0708] mb-4">{t("order.yourOrder")}</h3>

          {items.length === 0 ? (
            <div className="text-center py-10" data-testid="cart-empty-msg">
              <ShoppingBag size={36} className="text-[#C9A227] mx-auto mb-4" />
              <p className="text-sm text-[#6B4A4E] mb-6">{t("order.cartEmpty")}</p>
              <Link to="/menu"><button className="btn-saffron" data-testid="goto-menu-btn">{t("home.viewMenu")} <ArrowRight size={14} /></button></Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[#E8D9BE]">
                <span className="text-xs tracking-widest uppercase text-[#6B4A4E]">{t("order.tableHash")}</span>
                <input data-testid="table-number-input" type="number" min="1" value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} placeholder={t("order.tablePh")} className="w-28 bg-white border border-[#E8D9BE] rounded-md px-3 py-2 text-center font-serif-display text-2xl text-[#A90011] focus:border-[#C9A227] outline-none" />
              </div>

              <div className="space-y-3 max-h-72 overflow-y-auto pr-2">
                <AnimatePresence>
                  {items.map((i) => (
                    <motion.div key={i.menu_item_id} layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -20 }} className="flex items-center gap-3 pb-3 border-b border-dashed border-[#E8D9BE]" data-testid={`cart-item-${i.menu_item_id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#2A0708] truncate">{i.name}</p>
                        <p className="text-xs text-[#6B4A4E]">NT$ {i.price} × {i.quantity}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => updateQty(i.menu_item_id, i.quantity - 1)} className="p-1 rounded hover:bg-[#FBEBD8]" data-testid={`qty-minus-${i.menu_item_id}`}><Minus size={14} className="text-[#A90011]" /></button>
                        <span className="w-6 text-center text-[#2A0708]">{i.quantity}</span>
                        <button onClick={() => updateQty(i.menu_item_id, i.quantity + 1)} className="p-1 rounded hover:bg-[#FBEBD8]" data-testid={`qty-plus-${i.menu_item_id}`}><Plus size={14} className="text-[#A90011]" /></button>
                        <button onClick={() => removeItem(i.menu_item_id)} className="p-1 ml-1 rounded hover:bg-[#A90011]/10" data-testid={`remove-${i.menu_item_id}`}><Trash2 size={14} className="text-[#A90011]" /></button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              <div className="mt-4 space-y-2">
                <input placeholder={t("order.namePh")} value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="w-full bg-white border border-[#E8D9BE] rounded-md px-3 py-2 text-sm text-[#2A0708] outline-none focus:border-[#C9A227]" data-testid="customer-name-input" />
                <input placeholder={t("order.emailPh")} type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} className="w-full bg-white border border-[#E8D9BE] rounded-md px-3 py-2 text-sm text-[#2A0708] outline-none focus:border-[#C9A227]" data-testid="customer-email-input" />
                <textarea placeholder={t("order.notesPh")} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full bg-white border border-[#E8D9BE] rounded-md px-3 py-2 text-sm text-[#2A0708] outline-none focus:border-[#C9A227] resize-none" data-testid="order-notes-input" />
              </div>
              <div className="mt-5 pt-4 border-t border-[#E8D9BE]">
                <div className="flex justify-between mb-4">
                  <span className="font-serif-display text-xl text-[#2A0708]">{t("order.total")}</span>
                  <span className="font-serif-display text-2xl text-[#A90011]">NT$ {subtotal.toFixed(0)}</span>
                </div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#A90011] flex items-center gap-2 mb-4"><Banknote size={12} /> {t("order.cashOnly")}</p>
                <div className="flex gap-2">
                  <Link to="/menu" className="flex-1"><button className="btn-outline-gold w-full justify-center" data-testid="back-to-menu-btn">← {t("order.menu")}</button></Link>
                  <button onClick={submitOrder} disabled={submitting || !tableNumber} className="btn-saffron flex-1 justify-center disabled:opacity-50" data-testid="place-order-btn">
                    {submitting ? t("order.placing") : t("order.placeOrder")}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
